import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  BookOpen, BarChart2, FileText, CreditCard, Landmark, Briefcase,
  TrendingUp, Cpu, Check, ChevronRight, ArrowLeft, Trophy, Star,
  Lock, CheckCircle2, XCircle, GraduationCap, Award, ExternalLink,
  Clock, HelpCircle, Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'book-open': BookOpen, 'bar-chart-2': BarChart2, 'file-text': FileText,
  'credit-card': CreditCard, 'landmark': Landmark, 'briefcase': Briefcase,
  'trending-up': TrendingUp, 'cpu': Cpu,
};

export default function University() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [quizMode, setQuizMode] = useState(null); // 'module' | 'final'
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchModules = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/university/modules`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const openModule = async (moduleId) => {
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/university/modules/${moduleId}`);
      if (res.ok) { setActiveModule(await res.json()); setActiveLesson(null); }
    } catch (e) { console.error(e); }
  };

  const completeLesson = async (moduleId, lessonId) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/university/modules/${moduleId}/lessons/${lessonId}/complete`, { method: 'POST' });
    const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/university/modules/${moduleId}`);
    if (res.ok) setActiveModule(await res.json());
    fetchModules();
  };

  const startQuiz = async (moduleId) => {
    const url = moduleId === 'final'
      ? `${API_URL}/api/workspaces/${wsId}/university/final-exam`
      : `${API_URL}/api/workspaces/${wsId}/university/modules/${moduleId}/quiz`;
    const res = await apiFetch(url);
    if (res.ok) {
      const d = await res.json();
      setQuizData(d);
      setQuizAnswers(new Array(d.questions.length).fill(-1));
      setQuizResult(null);
      setQuizMode(moduleId === 'final' ? 'final' : 'module');
    }
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    const moduleId = quizMode === 'final' ? null : activeModule?.id;
    const url = quizMode === 'final'
      ? `${API_URL}/api/workspaces/${wsId}/university/final-exam/submit`
      : `${API_URL}/api/workspaces/${wsId}/university/modules/${moduleId}/quiz/submit`;
    try {
      const res = await apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers: quizAnswers }) });
      if (res.ok) {
        const result = await res.json();
        setQuizResult(result);
        if (result.passed) toast.success(`Passed with ${result.score}%!`);
        else toast.error(`Score: ${result.score}%. Need ${result.passing_score}% to pass.`);
        fetchModules();
        if (moduleId) openModule(moduleId);
      }
    } catch (e) { console.error(e); toast.error('Submit failed'); }
    finally { setSubmitting(false); }
  };

  // ─── Lesson Viewer ───
  if (activeLesson && activeModule) {
    const lessonIdx = activeModule.lessons.findIndex(l => l.id === activeLesson.id);
    const nextLesson = activeModule.lessons[lessonIdx + 1];
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="lesson-viewer">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setActiveLesson(null)} data-testid="back-to-module">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{activeModule.title}</p>
            <h1 className="text-xl font-bold tracking-tight truncate">{activeLesson.title}</h1>
          </div>
          <Badge variant={activeLesson.completed ? "default" : "secondary"} className={activeLesson.completed ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" : ""}>
            {activeLesson.completed ? 'Completed' : `Lesson ${lessonIdx + 1} of ${activeModule.lessons.length}`}
          </Badge>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="prose prose-sm dark:prose-invert max-w-none" data-testid="lesson-content">
              {activeLesson.content.split('\n\n').map((para, i) => {
                if (para.startsWith('**') && para.endsWith('**')) {
                  return <h3 key={i} className="text-base font-semibold mt-4 mb-2">{para.replace(/\*\*/g, '')}</h3>;
                }
                const parts = para.split(/(\*\*.*?\*\*)/g);
                return (
                  <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {parts.map((part, j) =>
                      part.startsWith('**') && part.endsWith('**')
                        ? <strong key={j} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>
                        : <span key={j}>{part.split('\n').map((line, k) => <span key={k}>{line}{k < part.split('\n').length - 1 && <br />}</span>)}</span>
                    )}
                  </p>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-3">
          {activeLesson.try_link && (
            <Button variant="outline" asChild data-testid="try-it-btn">
              <Link to={activeLesson.try_link}>
                <ExternalLink className="w-4 h-4 mr-2" />
                {activeLesson.try_label || 'Try it now'}
              </Link>
            </Button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {!activeLesson.completed && (
              <Button onClick={() => completeLesson(activeModule.id, activeLesson.id)} data-testid="complete-lesson-btn">
                <Check className="w-4 h-4 mr-2" />
                Mark Complete
              </Button>
            )}
            {nextLesson && (
              <Button variant={activeLesson.completed ? "default" : "outline"} onClick={() => setActiveLesson(nextLesson)} data-testid="next-lesson-btn">
                Next Lesson <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {!nextLesson && (
              <Button onClick={() => setActiveLesson(null)} data-testid="back-to-module-btn">
                Back to Module
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Quiz / Exam View ───
  if (quizData && quizMode) {
    const isExam = quizMode === 'final';
    const allAnswered = quizAnswers.every(a => a >= 0);
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid={isExam ? "final-exam" : "module-quiz"}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setQuizData(null); setQuizMode(null); setQuizResult(null); }} data-testid="back-from-quiz">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">{isExam ? 'Final Exam' : `${quizData.module_title} — Quiz`}</h1>
            <p className="text-xs text-muted-foreground">
              {quizData.questions.length} questions | {quizData.passing_score}% to pass
            </p>
          </div>
        </div>

        {quizResult ? (
          <Card className={quizResult.passed ? "border-emerald-500/50" : "border-red-500/50"}>
            <CardContent className="pt-6 text-center space-y-4">
              <div className={cn("w-20 h-20 rounded-full mx-auto flex items-center justify-center", quizResult.passed ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-red-100 dark:bg-red-900/30")}>
                {quizResult.passed ? <Trophy className="w-10 h-10 text-emerald-600 dark:text-emerald-400" /> : <XCircle className="w-10 h-10 text-red-500" />}
              </div>
              <h2 className="text-2xl font-bold">{quizResult.passed ? 'Congratulations!' : 'Not quite...'}</h2>
              <p className="text-4xl font-bold" style={{ color: quizResult.passed ? '#10b981' : '#ef4444' }}>{quizResult.score}%</p>
              <p className="text-muted-foreground">
                {quizResult.correct} of {quizResult.total} correct
                {!quizResult.passed && ` — Need ${quizResult.passing_score}% to pass`}
              </p>
              {quizResult.passed && isExam && (
                <div className="p-4 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800">
                  <Award className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="font-semibold text-amber-800 dark:text-amber-300">CountBucks Certified!</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">You've completed all modules and passed the final exam.</p>
                </div>
              )}

              <Separator />
              <div className="space-y-2 text-left">
                {quizResult.results.map((r, i) => (
                  <div key={i} className={cn("flex items-start gap-2 p-2 rounded text-sm", r.correct ? "bg-emerald-50 dark:bg-emerald-900/10" : "bg-red-50 dark:bg-red-900/10")}>
                    {r.correct ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                    <div>
                      <p className="font-medium">{quizData.questions[i].question}</p>
                      {!r.correct && <p className="text-xs text-muted-foreground mt-0.5">Correct: {quizData.questions[i].options[r.correct_answer]}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 justify-center pt-2">
                {!quizResult.passed && (
                  <Button onClick={() => { setQuizResult(null); setQuizAnswers(new Array(quizData.questions.length).fill(-1)); }} data-testid="retry-quiz">
                    Retry
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setQuizData(null); setQuizMode(null); setQuizResult(null); }} data-testid="done-quiz">
                  {quizResult.passed ? 'Done' : 'Go Back'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              {quizData.questions.map((q, qi) => (
                <Card key={qi} data-testid={`quiz-question-${qi}`}>
                  <CardContent className="pt-5 space-y-3">
                    <p className="text-sm font-semibold">
                      <span className="text-muted-foreground mr-2">{qi + 1}.</span>
                      {q.question}
                    </p>
                    <div className="grid gap-2">
                      {q.options.map((opt, oi) => (
                        <button
                          key={oi}
                          onClick={() => { const a = [...quizAnswers]; a[qi] = oi; setQuizAnswers(a); }}
                          className={cn(
                            "text-left p-3 rounded-lg border text-sm transition-all",
                            quizAnswers[qi] === oi
                              ? "border-primary bg-primary/5 font-medium"
                              : "border-border hover:border-primary/30 hover:bg-muted/50"
                          )}
                          data-testid={`quiz-option-${qi}-${oi}`}
                        >
                          <span className="text-muted-foreground mr-2 font-mono text-xs">{String.fromCharCode(65 + oi)}.</span>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {quizAnswers.filter(a => a >= 0).length} of {quizData.questions.length} answered
              </p>
              <Button onClick={submitQuiz} disabled={!allAnswered || submitting} data-testid="submit-quiz">
                {submitting ? 'Submitting...' : 'Submit Answers'}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Module Detail ───
  if (activeModule) {
    const allLessonsDone = activeModule.lessons.every(l => l.completed);
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="module-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setActiveModule(null)} data-testid="back-to-modules">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: activeModule.color + '20' }}>
            {(() => { const I = ICON_MAP[activeModule.icon] || BookOpen; return <I className="w-5 h-5" style={{ color: activeModule.color }} />; })()}
          </div>
          <div>
            <h1 className="text-xl font-bold">{activeModule.title}</h1>
            <p className="text-xs text-muted-foreground">{activeModule.description}</p>
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Lessons</CardTitle></CardHeader>
          <CardContent className="p-0">
            {activeModule.lessons.map((les, i) => (
              <button
                key={les.id}
                onClick={() => setActiveLesson(les)}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors border-b last:border-0 text-left"
                data-testid={`lesson-${les.id}`}
              >
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold", les.completed ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground")}>
                  {les.completed ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-medium", les.completed && "text-muted-foreground")}>{les.title}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className={activeModule.quiz_passed ? "border-emerald-500/30" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", activeModule.quiz_passed ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted")}>
                  {activeModule.quiz_passed ? <Trophy className="w-5 h-5 text-emerald-600" /> : <HelpCircle className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div>
                  <p className="font-semibold text-sm">Module Quiz</p>
                  <p className="text-xs text-muted-foreground">
                    {activeModule.quiz_passed
                      ? `Passed with ${activeModule.quiz_score}%`
                      : `${activeModule.quiz_questions} questions | 70% to pass`}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => startQuiz(activeModule.id)}
                disabled={!allLessonsDone}
                variant={activeModule.quiz_passed ? "outline" : "default"}
                data-testid="start-quiz-btn"
              >
                {!allLessonsDone ? <><Lock className="w-4 h-4 mr-2" />Complete lessons first</> :
                 activeModule.quiz_passed ? 'Retake Quiz' : 'Start Quiz'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Module Grid (Home) ───
  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>;

  return (
    <div className="space-y-6" data-testid="university-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="university-heading">CountBucks University</h1>
            <p className="text-sm text-muted-foreground">Business & Accounting Education</p>
          </div>
        </div>
        {data?.certified && (
          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1.5 px-3 py-1.5" data-testid="certified-badge">
            <Award className="w-4 h-4" /> Certified
          </Badge>
        )}
      </div>

      {data && (
        <Card className="bg-gradient-to-r from-indigo-50 via-background to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{data.completed_modules} of {data.total_modules} modules complete</span>
              <span className="text-sm font-bold" style={{ color: data.completed_modules === data.total_modules ? '#10b981' : '#6366f1' }}>
                {Math.round((data.completed_modules / data.total_modules) * 100)}%
              </span>
            </div>
            <Progress value={(data.completed_modules / data.total_modules) * 100} className="h-2" />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.modules.map((mod) => {
          const Icon = ICON_MAP[mod.icon] || BookOpen;
          const progress = mod.total_lessons > 0 ? Math.round((mod.completed_lessons / mod.total_lessons) * 100) : 0;
          return (
            <Card
              key={mod.id}
              className={cn("cursor-pointer hover:border-primary/30 transition-all group", mod.is_complete && "border-emerald-500/30")}
              onClick={() => openModule(mod.id)}
              data-testid={`module-card-${mod.id}`}
            >
              <CardContent className="pt-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: mod.color + '15' }}>
                    <Icon className="w-5 h-5" style={{ color: mod.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm">{mod.title}</h3>
                      {mod.is_complete && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{mod.description}</p>
                    <div className="mt-3 space-y-1.5">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-muted-foreground">{mod.completed_lessons}/{mod.total_lessons} lessons</span>
                        <span className={cn("font-medium", mod.quiz_passed ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
                          {mod.quiz_passed ? `Quiz: ${mod.quiz_score}%` : 'Quiz: Not taken'}
                        </span>
                      </div>
                      <Progress value={progress} className="h-1" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Final Exam Card */}
      {data && (
        <Card className={cn("border-2", data.final_exam_unlocked ? "border-amber-500/50 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20" : "border-dashed opacity-60")} data-testid="final-exam-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", data.final_exam_unlocked ? "bg-amber-100 dark:bg-amber-900/30" : "bg-muted")}>
                  {data.final_exam_unlocked ? <Trophy className="w-6 h-6 text-amber-600" /> : <Lock className="w-6 h-6 text-muted-foreground" />}
                </div>
                <div>
                  <h3 className="font-bold">Final Exam</h3>
                  <p className="text-xs text-muted-foreground">
                    {data.final_exam_passed
                      ? `Passed with ${data.final_exam_score}% — You're certified!`
                      : data.final_exam_unlocked
                        ? '20 questions across all modules | 70% to pass'
                        : 'Complete all 8 modules to unlock'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => startQuiz('final')}
                disabled={!data.final_exam_unlocked}
                variant={data.final_exam_passed ? "outline" : "default"}
                data-testid="start-final-exam"
              >
                {data.final_exam_passed ? 'Retake Exam' : data.final_exam_unlocked ? 'Start Exam' : <><Lock className="w-4 h-4 mr-2" />Locked</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
